import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Facebook, Instagram, ClipboardList, Smartphone, Download } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useToast } from '../hooks/use-toast';
import { contactEmails, heroImages } from '../mock';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [qa, setQa] = useState({
    name: '', email: '', phone: '', division: '',
    q1: '', q2: '', q3: '', q4: '', q5: '', feedback: ''
  });
  const { toast } = useToast();

  const FEEDBACK_TO = contactEmails.join(',');

  const buildMailto = (data) => {
    const subject = `Patriots Program Q&A — ${data.name || 'Anonymous'}`;
    const bodyLines = [
      'BROOKLINE JAMAICA PLAIN PATRIOTS — Program Feedback',
      '=================================================',
      '',
      `Name: ${data.name || '(not provided)'}`,
      `Email: ${data.email || '(not provided)'}`,
      `Phone: ${data.phone || '(not provided)'}`,
      `Scholar Division: ${data.division || '(not provided)'}`,
      '',
      '— QUESTIONS —',
      `Q1. What is your child enjoying most about the program?`,
      `    ${data.q1 || '(no answer)'}`,
      '',
      `Q2. What could the coaches/staff improve on?`,
      `    ${data.q2 || '(no answer)'}`,
      '',
      `Q3. How are practices/game-days working for your family schedule?`,
      `    ${data.q3 || '(no answer)'}`,
      '',
      `Q4. What community events / fundraisers would you love to see?`,
      `    ${data.q4 || '(no answer)'}`,
      '',
      `Q5. Would you recommend the program to another family? Why?`,
      `    ${data.q5 || '(no answer)'}`,
      '',
      '— ADDITIONAL FEEDBACK —',
      data.feedback || '(none)',
      '',
      'Sent from the "ICE" Brookline JP Patriots website — Go Patriots!'
    ];
    const body = bodyLines.join('\n');
    return `mailto:${FEEDBACK_TO}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const qaHref = buildMailto(qa);
  const qrHref = typeof window !== 'undefined' ? `${window.location.origin}/contact#program-qa` : '';

  const submit = (e) => {
    e.preventDefault();
    if (!form.email) return;
    toast({ title: 'Message sent!', description: 'A Patriots team member will get back to you soon.' });
    setForm({ name: '', email: '', message: '' });
  };

  const submitQa = (e) => {
    e.preventDefault();
    if (!qa.name || !qa.email) {
      toast({ title: 'Missing info', description: 'Please include at least your name and email so we can follow up.' });
      return;
    }
    // Open user's email client with the answers pre-filled
    window.location.href = buildMailto(qa);
    toast({
      title: 'Thank you!',
      description: 'Your email client is opening with your answers ready to send. Go Patriots!',
      duration: 6000
    });
  };

  const updQa = (k) => (e) => setQa({ ...qa, [k]: e.target.value });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="font-body">
      {/* Header */}
      <section className="relative pt-40 pb-24 bg-[#0a1f44] overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImages[0]} alt="team" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1f44]/70 to-[#0a1f44]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Get In Touch</div>
          <h1 className="font-display text-white text-6xl md:text-8xl leading-none">CONTACT US</h1>
          <p className="text-white/70 max-w-2xl mt-6 text-lg">
            Questions about registration, sponsorship, or joining the Patriots family? Drop us a line.
          </p>
        </div>
      </section>

      <div className="stripe-sep" />

      {/* Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-12">
          {/* Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-3">Program Contact</div>
              <h2 className="font-display text-3xl md:text-4xl text-[#0a1f44] mb-2">JEFF HAMMOND<br />&quot;ICE&quot; BROOKLINE JAMAICA PLAIN<br />POP WARNER FOOTBALL &amp; CHEERLEADING</h2>
              <p className="text-[#0a1f44]/70">Reach out to Program President Jeff Hammond for any questions.</p>
            </div>

            <div className="space-y-4">
              <a href="tel:6178188296" data-testid="contact-phone" className="hover-lift block p-5 border-2 border-[#0a1f44]/10 hover:border-[#c8102e]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#c8102e] text-white flex items-center justify-center"><Phone className="w-5 h-5" /></div>
                  <div>
                    <div className="font-heading uppercase tracking-widest text-xs text-[#0a1f44]/60">Phone</div>
                    <div className="font-display text-2xl text-[#0a1f44] tracking-wide">617-818-8296</div>
                    <div className="text-xs text-[#0a1f44]/60">Jeff Hammond</div>
                  </div>
                </div>
              </a>

              <div className="hover-lift p-5 border-2 border-[#0a1f44]/10 hover:border-[#c8102e]" data-testid="contact-email-list">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#c8102e] text-white flex items-center justify-center"><Mail className="w-5 h-5" /></div>
                  <div className="min-w-0">
                    <div className="font-heading uppercase tracking-widest text-xs text-[#0a1f44]/60 mb-1">Email</div>
                    {contactEmails.map((email, index) => (
                      <a key={email} href={`mailto:${email}`} data-testid={`contact-email-${index + 1}`} className="block font-heading text-sm sm:text-base text-[#0a1f44] font-semibold break-all hover:text-[#c8102e] transition-colors">
                        {email}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-5 border-2 border-[#0a1f44]/10">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#c8102e] text-white flex items-center justify-center"><MapPin className="w-5 h-5" /></div>
                  <div>
                    <div className="font-heading uppercase tracking-widest text-xs text-[#0a1f44]/60">Location</div>
                    <div className="font-heading text-lg text-[#0a1f44] font-semibold">Brookline / Jamaica Plain, MA</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-3">Connect With Us</div>
              <div className="flex gap-3">
                <a href="https://www.facebook.com/627656147646539" target="_blank" rel="noreferrer" className="w-12 h-12 border-2 border-[#0a1f44]/20 hover:border-[#c8102e] hover:bg-[#c8102e] hover:text-white flex items-center justify-center transition-all">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/brooklinejppats" target="_blank" rel="noreferrer" className="w-12 h-12 border-2 border-[#0a1f44]/20 hover:border-[#c8102e] hover:bg-[#c8102e] hover:text-white flex items-center justify-center transition-all">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-[#0a1f44] p-5 sm:p-8 md:p-12 relative overflow-hidden">
              <div className="absolute inset-0 stars-bg" />
              <div className="relative">
                <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-3">Drop Us A Line</div>
                <h3 className="font-display text-white text-4xl mb-8">SEND A MESSAGE</h3>
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div>
                    <label className="font-heading uppercase tracking-widest text-xs text-white/70 block mb-2">Name</label>
                    <input type="text" value={form.name} onChange={upd('name')} data-testid="contact-form-name" autoComplete="name" className="w-full bg-white/5 border border-white/20 focus:border-[#c8102e] outline-none text-white px-4 py-3" />
                  </div>
                  <div>
                    <label className="font-heading uppercase tracking-widest text-xs text-white/70 block mb-2">Email *</label>
                    <input required type="email" value={form.email} onChange={upd('email')} data-testid="contact-form-email" autoComplete="email" inputMode="email" className="w-full bg-white/5 border border-white/20 focus:border-[#c8102e] outline-none text-white px-4 py-3" />
                  </div>
                  <div>
                    <label className="font-heading uppercase tracking-widest text-xs text-white/70 block mb-2">Message</label>
                    <textarea rows={5} value={form.message} onChange={upd('message')} data-testid="contact-form-message" className="w-full bg-white/5 border border-white/20 focus:border-[#c8102e] outline-none text-white px-4 py-3 resize-none" />
                  </div>
                  <button type="submit" data-testid="contact-form-submit" className="group bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest px-8 py-4 flex items-center gap-2 transition-colors">
                    Send <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAM Q&A FEEDBACK */}
      <section id="program-qa" className="py-24 bg-[#f5f6f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <ClipboardList className="w-10 h-10 text-[#c8102e] mx-auto mb-4" />
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Help Us Grow</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">PROGRAM FEEDBACK Q&amp;A</h2>
            <p className="text-[#0a1f44]/70 mt-4">
              Answer a few quick questions and hit send — your answers open your email app pre-filled and go to <span className="font-heading text-[#c8102e]">{contactEmails.join(' and ')}</span>.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Form (left, spans 3) */}
            <form onSubmit={submitQa} className="lg:col-span-3 bg-white shadow-sm p-5 sm:p-8 space-y-5" data-testid="program-qa-form">
              <div className="grid md:grid-cols-2 gap-4">
                <QaField label="Your Name *" value={qa.name} onChange={updQa('name')} testid="qa-name" required />
                <QaField label="Email *" value={qa.email} onChange={updQa('email')} testid="qa-email" type="email" required />
                <QaField label="Phone" value={qa.phone} onChange={updQa('phone')} testid="qa-phone" type="tel" />
                <QaField label="Scholar's Division" value={qa.division} onChange={updQa('division')} testid="qa-division" placeholder="e.g. U9, U11, U13, Varsity Cheer" />
              </div>

              <QaTextarea label="1. What is your child enjoying most about the program?" value={qa.q1} onChange={updQa('q1')} testid="qa-q1" />
              <QaTextarea label="2. What could the coaches / staff improve on?" value={qa.q2} onChange={updQa('q2')} testid="qa-q2" />
              <QaTextarea label="3. How are practices &amp; game-days working for your family schedule?" value={qa.q3} onChange={updQa('q3')} testid="qa-q3" />
              <QaTextarea label="4. What community events or fundraisers would you love to see?" value={qa.q4} onChange={updQa('q4')} testid="qa-q4" />
              <QaTextarea label="5. Would you recommend the Patriots to another family? Why or why not?" value={qa.q5} onChange={updQa('q5')} testid="qa-q5" />

              <div>
                <label className="font-heading uppercase tracking-widest text-xs text-[#0a1f44]/70 block mb-2">Anything else you&apos;d like the Board to hear?</label>
                <textarea
                  rows={4}
                  value={qa.feedback}
                  onChange={updQa('feedback')}
                  data-testid="qa-feedback"
                  className="w-full border border-[#0a1f44]/20 px-4 py-3 focus:outline-none focus:border-[#c8102e]"
                  placeholder="Additional feedback, suggestions, or ideas…"
                />
              </div>

              <div className="bg-[#0a1f44] text-white p-5 text-center">
                <p className="text-sm leading-relaxed">
                  We appreciate your help in making the program a better place for your children to grow.
                  <span className="block font-display text-xl text-[#c8102e] mt-2">GO PATRIOTS!</span>
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <button type="submit" data-testid="qa-submit" className="group bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest px-8 py-4 inline-flex items-center gap-2 transition-colors">
                  <Send className="w-4 h-4" /> Send to Program
                </button>
                <a href={qaHref} data-testid="qa-download-mailto" className="bg-white border-2 border-[#0a1f44] hover:bg-[#0a1f44] hover:text-white text-[#0a1f44] font-heading uppercase tracking-widest px-6 py-4 inline-flex items-center gap-2 transition-colors">
                  <Download className="w-4 h-4" /> Open in Email App
                </a>
              </div>
            </form>

            {/* QR (right, spans 2) */}
            <aside className="lg:col-span-2 bg-white shadow-sm p-8 flex flex-col items-center text-center">
              <Smartphone className="w-8 h-8 text-[#c8102e] mb-3" />
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-3">Scan &amp; Fill On Phone</div>
              <h3 className="font-display text-[#0a1f44] text-3xl mb-5 leading-tight">TAKE IT MOBILE</h3>
              <div className="bg-white p-3 border-2 border-[#0a1f44] mb-5">
                <QRCodeCanvas value={qrHref} size={180} bgColor="#ffffff" fgColor="#0a1f44" level="M" includeMargin={false} />
              </div>
              <p className="text-sm text-[#0a1f44]/70 leading-relaxed">
                Point your phone camera at the QR code to open the Q&amp;A form on your phone. Fill it out, tap send, and it goes straight to
                <span className="block font-heading text-[#c8102e] mt-1 break-all">{contactEmails.join(' · ')}</span>
              </p>
              <a
                href={qaHref}
                data-testid="qa-mobile-mailto"
                className="mt-6 w-full bg-[#0a1f44] hover:bg-[#0a1f44]/90 text-white font-heading uppercase tracking-widest text-sm px-6 py-3 inline-flex items-center justify-center gap-2 transition-colors"
              >
                <Mail className="w-4 h-4" /> Email Now
              </a>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}

function QaField({ label, value, onChange, testid, type = 'text', required = false, placeholder = '' }) {
  const inputMode = type === 'email' ? 'email' : type === 'tel' ? 'tel' : undefined;
  const autoComplete = type === 'email' ? 'email' : type === 'tel' ? 'tel' : undefined;
  return (
    <div>
      <label className="font-heading uppercase tracking-widest text-xs text-[#0a1f44]/70 block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        data-testid={testid}
        className="w-full border border-[#0a1f44]/20 px-4 py-3 focus:outline-none focus:border-[#c8102e]"
      />
    </div>
  );
}

function QaTextarea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="font-heading uppercase tracking-widest text-xs text-[#0a1f44]/70 block mb-2" dangerouslySetInnerHTML={{ __html: label }} />
      <textarea
        rows={2}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full border border-[#0a1f44]/20 px-4 py-3 focus:outline-none focus:border-[#c8102e]"
      />
    </div>
  );
}
